/**
 * Who approves, in what order.
 *
 * A chain is a list of named people — not roles — each with the capacity
 * they sign in (reviewer, approver, administrator). The backend checks the
 * chain against the risk level; the rule is shown here as it is built so
 * the person is not told after the fact.
 */

import { useMembers, type Member } from '../admin/useMembers';
import { CHAIN_ROLE_LABEL, CHAIN_ROLES, chainProblem, type ChainRole, type RiskLevel } from './types';
import { EXC_CHAIN_MAX } from '../../types/fieldLimits';
import type { ChainStepInput } from './useExceptions';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Exceptions.module.css';

export interface ChainDraft {
  uid: string;
  role: ChainRole;
}

export function toChainInput(draft: readonly ChainDraft[], members: readonly Member[]): ChainStepInput[] {
  return draft.map((d) => {
    const m = members.find((x) => x.uid === d.uid);
    const step: ChainStepInput = { approverUid: d.uid, role: d.role };
    if (m?.email) step.approverEmail = m.email;
    if (m?.displayName) step.approverDisplayName = m.displayName;
    return step;
  });
}

export function ApprovalChainBuilder({
  orgId, risk, value, onChange, idPrefix,
}: {
  orgId: string | null;
  risk: RiskLevel;
  value: ChainDraft[];
  onChange: (next: ChainDraft[]) => void;
  idPrefix: string;
}) {
  const members = useMembers(orgId);
  const people = (members.data ?? []).filter((m) => m.status === 'active');
  const problem = chainProblem(risk, value);
  const full = value.length >= EXC_CHAIN_MAX;

  function set(i: number, patch: Partial<ChainDraft>) {
    onChange(value.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  }

  return (
    <div>
      <ol className={own.chain} aria-label="Approval steps">
        {value.map((step, i) => (
          <li key={i} className={own.chainStep}>
            <span className={own.stepNo}>{i + 1}</span>
            <div className={form.row} style={{ flex: 1 }}>
              <div>
                <label className={form.label} htmlFor={`${idPrefix}-who-${i}`}>Who</label>
                <select id={`${idPrefix}-who-${i}`} className={form.select} value={step.uid}
                  onChange={(e) => set(i, { uid: e.target.value })}>
                  <option value="">Choose a person</option>
                  {people.map((m) => (
                    <option key={m.uid} value={m.uid}>{m.displayName}{m.email ? ` — ${m.email}` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={form.label} htmlFor={`${idPrefix}-as-${i}`}>Signs as</label>
                <select id={`${idPrefix}-as-${i}`} className={form.select} value={step.role}
                  onChange={(e) => set(i, { role: e.target.value as ChainRole })}>
                  {CHAIN_ROLES.map((r) => <option key={r} value={r}>{CHAIN_ROLE_LABEL[r]}</option>)}
                </select>
              </div>
            </div>
            <button type="button" className={own.remove} aria-label={`Remove step ${i + 1}`}
              onClick={() => onChange(value.filter((_, j) => j !== i))}>
              <span aria-hidden="true">×</span>
            </button>
          </li>
        ))}
      </ol>
      <div className={styles.actions} style={{ marginTop: value.length ? 'var(--sp-3)' : 0 }}>
        <button type="button" className={styles.btn} disabled={full}
          onClick={() => onChange([...value, { uid: '', role: 'approver' }])}>
          Add a step
        </button>
        <span className={form.hint} style={{ margin: 0 }}>
          {full ? `Up to ${EXC_CHAIN_MAX} steps.` : 'Approvers decide in this order.'}
        </span>
      </div>
      <p className={problem ? form.error : form.hint} role="status">
        {problem ?? 'This chain meets what the risk level requires.'}
        {members.isLoading && ' Loading people…'}
      </p>
    </div>
  );
}

/** Every step has a person, and the chain satisfies the risk rule. */
export function chainReady(risk: RiskLevel, value: readonly ChainDraft[]): boolean {
  return value.length > 0 && value.every((s) => s.uid) && chainProblem(risk, value) === null;
}
